import io, { Socket } from 'socket.io-client';

type Listener = (...args: any[]) => void;

/**
 * A socket.io-compatible facade that keeps ONE real connection for every tab of
 * the app in the same browser.
 *
 * The tab that wins the Web Lock becomes the leader and owns the real socket.
 * Every inbound event is fanned out to all tabs over a BroadcastChannel, and
 * emits from other tabs are relayed to the leader. If the leader tab closes the
 * lock is released, the next tab takes over and reconnects.
 *
 * Exposes the subset of the Socket API the app uses: on/off/emit/connect/
 * disconnect/removeAllListeners, `connected`, `id` and a stub `io` manager.
 * Browsers without Web Locks/BroadcastChannel fall back to a private socket.
 */
export class SharedSocket {
    private static readonly LOCK_NAME = 'enterprise-socket-leader';
    private static readonly CHANNEL_NAME = 'enterprise-socket-channel';
    private static readonly RESERVED = ['connect', 'disconnect', 'connect_error'];

    private readonly tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    private readonly listeners = new Map<string, Set<Listener>>();
    private readonly pendingAcks = new Map<string, Listener>();
    private channel?: BroadcastChannel;
    private real?: Socket;
    private isLeader = false;
    private remoteConnected = false;
    private remoteId: string | undefined;
    private ackSeq = 0;
    private stateKnown = true;

    /** Stub of socket.io's Manager so `socket.io?.on('reconnect')` callers stay valid. */
    public readonly io = { on: () => {}, off: () => {} };

    constructor(private readonly url: string, private readonly options: any) {
        const canShare = typeof BroadcastChannel !== 'undefined' && !!(navigator as any).locks;
        if (!canShare) {
            this.becomeLeader(false);
            return;
        }
        this.channel = new BroadcastChannel(SharedSocket.CHANNEL_NAME);
        this.stateKnown = false;
        // Don't wait forever if no leader ever answers.
        setTimeout(() => (this.stateKnown = true), 2000);
        this.channel.onmessage = (e) => this.onMessage(e.data);
        // Ask whoever leads for the current state, then queue for leadership.
        this.channel.postMessage({ t: 'hello' });
        (navigator as any).locks.request(SharedSocket.LOCK_NAME, () => {
            this.becomeLeader(true);
            // Hold the lock for the tab's lifetime; released when the tab closes.
            return new Promise<void>(() => {});
        });
    }

    /**
     * False for a non-leader tab until the leader has reported its state (a few
     * ms after load). Until then `connected` is a placeholder, not a real "down".
     */
    get stateResolved(): boolean {
        return this.isLeader || this.stateKnown;
    }

    get connected(): boolean {
        return this.isLeader ? !!this.real?.connected : this.remoteConnected;
    }

    get id(): string | undefined {
        return this.isLeader ? this.real?.id : this.remoteId;
    }

    on(event: string, fn: Listener): this {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(fn);
        return this;
    }

    off(event?: string, fn?: Listener): this {
        if (!event) {
            this.listeners.clear();
        } else if (!fn) {
            this.listeners.delete(event);
        } else {
            this.listeners.get(event)?.delete(fn);
        }
        return this;
    }

    removeAllListeners(event?: string): this {
        return this.off(event);
    }

    connect(): this {
        if (this.isLeader) {
            if (this.real && !this.real.connected) {
                this.real.connect();
            }
        } else {
            this.channel?.postMessage({ t: 'cmd', cmd: 'connect' });
        }
        return this;
    }

    disconnect(): this {
        if (this.isLeader) {
            this.real?.disconnect();
        } else {
            this.channel?.postMessage({ t: 'cmd', cmd: 'disconnect' });
        }
        return this;
    }

    emit(event: string, ...args: any[]): this {
        if (this.isLeader) {
            this.real?.emit(event, ...args);
            return this;
        }
        let ackId: string | undefined;
        if (typeof args[args.length - 1] === 'function') {
            ackId = `${this.tabId}:${++this.ackSeq}`;
            this.pendingAcks.set(ackId, args.pop());
        }
        this.channel?.postMessage({ t: 'emit', event, args, ackId, from: this.tabId });
        return this;
    }

    // ---- leader side ----

    private becomeLeader(shared: boolean) {
        this.isLeader = true;
        const real = io(this.url, this.options);
        this.real = real;

        SharedSocket.RESERVED.forEach((ev) =>
            real.on(ev, (...args: any[]) => {
                this.dispatch(ev, args);
                // connect/disconnect reach other tabs as state transitions (see
                // onMessage); only connect_error needs forwarding as an event.
                this.broadcastState(ev === 'disconnect' ? args[0] : undefined);
                if (shared && ev === 'connect_error') {
                    this.channel?.postMessage({ t: 'ev', event: ev, args: [] });
                }
            })
        );
        real.on('error', (...args: any[]) => this.dispatch('error', args));
        real.onAny((event: string, ...args: any[]) => {
            this.dispatch(event, args);
            if (shared) {
                this.channel?.postMessage({ t: 'ev', event, args });
            }
        });
        this.broadcastState();
    }

    private broadcastState(reason?: string) {
        this.channel?.postMessage({ t: 'state', connected: !!this.real?.connected, id: this.real?.id, reason });
    }

    private onMessage(msg: any) {
        if (!msg) return;
        if (this.isLeader) {
            switch (msg.t) {
                case 'hello':
                    this.broadcastState();
                    break;
                case 'emit': {
                    const args = msg.args || [];
                    if (msg.ackId) {
                        args.push((...res: any[]) =>
                            this.channel?.postMessage({ t: 'ack', ackId: msg.ackId, to: msg.from, args: res })
                        );
                    }
                    this.real?.emit(msg.event, ...args);
                    break;
                }
                case 'cmd':
                    if (msg.cmd === 'connect') this.connect();
                    if (msg.cmd === 'disconnect') this.real?.disconnect();
                    break;
            }
            return;
        }
        switch (msg.t) {
            case 'state': {
                const was = this.remoteConnected;
                this.stateKnown = true;
                this.remoteConnected = msg.connected;
                this.remoteId = msg.id;
                // Turn state changes into connect/disconnect events. This also covers
                // a tab opened while the socket is already up, which would otherwise
                // never see a 'connect' and keep showing the terminal as unreachable.
                if (!was && msg.connected) this.dispatch('connect', []);
                else if (was && !msg.connected) this.dispatch('disconnect', [msg.reason || 'transport close']);
                break;
            }
            case 'ev':
                this.dispatch(msg.event, msg.args || []);
                break;
            case 'ack': {
                if (msg.to !== this.tabId) break;
                const cb = this.pendingAcks.get(msg.ackId);
                this.pendingAcks.delete(msg.ackId);
                cb?.(...(msg.args || []));
                break;
            }
        }
    }

    private dispatch(event: string, args: any[]) {
        this.listeners.get(event)?.forEach((fn) => {
            try {
                fn(...args);
            } catch (err) {
                console.error(`Socket listener for "${event}" failed`, err);
            }
        });
    }
}
